'use client';

import {
  FeaturedContent,
  LinkTile,
  type TLinkTile,
} from '@chemist4u/newt-components';
import { storeName } from '@lib/config/brand';
import { composeThemeToken } from '@lib/mappers';
import SkeletonFeaturedCategories from '@modules/skeletons/components/skeleton-featured-categories';
import * as Sentry from '@sentry/nextjs';
import { useEffect, useState } from 'react';
import type {
  CategoryImage,
  StoreProductCategoryWithImage,
} from 'types/global';
import { getFeaturedCategories } from './actions';

const MAX_CATEGORIES = 6;

function findThumbnailImage(images: CategoryImage[]) {
  return images.find((img) => img.type === 'thumbnail') ?? images[0];
}

function mapCategoryToLinkTile(
  category: StoreProductCategoryWithImage,
): TLinkTile {
  const thumbnail = findThumbnailImage(category.product_category_image ?? []);

  const props: TLinkTile = {
    href: `/${category.handle}`,
    label: category.name,
  };

  if (thumbnail?.url) {
    props.image = {
      url: thumbnail.url,
      alt: category.name,
    };
  }

  return props;
}

export default function FeaturedCategoriesSection() {
  const [categories, setCategories] = useState<
    StoreProductCategoryWithImage[] | null
  >(null);

  useEffect(() => {
    let cancelled = false;

    getFeaturedCategories()
      .then((result) => {
        if (!cancelled) {
          setCategories(result);
        }
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        // `listCategoriesByHandles` already reports its own fetch failures —
        // this catches the separate case of the Server Action call itself
        // failing (network drop, ad-blocker/CSP interference), which never
        // reaches that inner try/catch.
        Sentry.captureException(error, {
          extra: { context: 'FeaturedCategoriesSection:getFeaturedCategories' },
        });
        setCategories([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (categories === null) {
    return <SkeletonFeaturedCategories />;
  }

  if (categories.length === 0) {
    return null;
  }

  const visible = categories.slice(0, MAX_CATEGORIES);

  return (
    <div data-theme={composeThemeToken(storeName, 'neutral-default')}>
      <FeaturedContent
        layout="grid"
        itemGridSize="wide"
        heading={{
          tag: 'h2',
          children: 'How can our pharmacists help you today?',
        }}
      >
        {visible.map((category) => (
          <LinkTile key={category.id} {...mapCategoryToLinkTile(category)} />
        ))}
      </FeaturedContent>
    </div>
  );
}
